/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isEmpty from 'lodash.isempty';
import {getAccountLineage} from '@ion/core/src/entities/Accounts';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {AccountIdLinkMap} from './types';

export interface DynamicLookupHierarchyTooltipOptionProps {
  accountTree: AccountIdLinkMap;
  item: {
    accountId?: number;
    name: string;
  };
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  label : {
    color        : dlsColor('grey1'),
    width        : '100%',
    whiteSpace   : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  },
  parentAccount : {
    color      : dlsColor('grey4'),
    fontFamily : 'Neo Sans Std',
    fontStyle  : 'italic'
  }
}));

/**
 * This component is used to display the hierarchy of the item in the DynamicLookUp component.
 * If the item has a parent, the parent name is displayed along with the item name.
 * If the item does not have a parent, only the item name is displayed.
 * On hover, the full hierarchy of the item is displayed in a tooltip.
 *
 * ```typescript
 *
 * import {DynamicLookupHierarchyTooltip} from '@ion/features/src/search/DynamicLookupHierarchyTooltip';
 *
 * <DynamicLookUp>
 * ...
 *   <Option>
 *     <DynamicLookupHierarchyTooltip accountTree={accountTree} item={item} />
 *   </Option>
 * ...
 * </DynamicLookUp>
 *
 * @accountTree - The account tree object.
 * @item        - The item object.
 */
export const DynamicLookupHierarchyTooltipOption = ({
  accountTree,
  item
}: DynamicLookupHierarchyTooltipOptionProps) => {
  const classes          = useStyles();
  const parentHierarchy  = getAccountLineage(item?.accountId, accountTree);
  const immediateParent  = !isEmpty(parentHierarchy) ? parentHierarchy[0] : null;
  const {name}           = item || {};

  const tooltipText = useMemo(() => {
    return parentHierarchy.length > 0 ? [...parentHierarchy].reverse().join(' → ') : '';
  }, [parentHierarchy]);

  return (
    <>
      <ConditionalRender condition={isEmpty(parentHierarchy)}>
        <span>{name}</span>
      </ConditionalRender>
      <ConditionalRender condition={!isEmpty(parentHierarchy)}>
        <Tooltip title={<span>{tooltipText}</span>}
          placement={'top'}
          enterDelay={500}
          arrow>
          <span className={classes.label}>
            {name}
            <ConditionalRender condition={immediateParent}>
              <span className={classes.parentAccount}>
                {` (${immediateParent})`}
              </span>
            </ConditionalRender>
          </span>
        </Tooltip>
      </ConditionalRender>
    </>
  );
};

export default memo(DynamicLookupHierarchyTooltipOption);
